import {
  FileText,
  Image,
  Minimize2,
  RefreshCw,
  Zap,
  Shield,
} from 'lucide-react';
import { useInView } from '../hooks/useInView';

const features = [
  {
    icon: FileText,
    title: 'PDF Form Builder',
    description:
      'Add fillable text inputs, checkboxes, and signature fields to any existing PDF document with ease.',
    color: '#D63031',
    bg: 'rgba(214, 48, 49, 0.08)',
    border: 'rgba(214, 48, 49, 0.15)',
  },
  {
    icon: Minimize2,
    title: 'Image Compression',
    description:
      'Reduce image file sizes dramatically without a noticeable drop in quality, perfect for web and sharing.',
    color: '#16A34A',
    bg: 'rgba(22, 163, 74, 0.08)',
    border: 'rgba(22, 163, 74, 0.15)',
  },
  {
    icon: Image,
    title: 'Image to PNG',
    description:
      'Convert JPG, WebP, GIF, and other formats to lossless PNG in seconds with a single click.',
    color: '#0C66FA',
    bg: 'rgba(12, 102, 250, 0.08)',
    border: 'rgba(12, 102, 250, 0.15)',
  },
  {
    icon: RefreshCw,
    title: 'Image to JPG',
    description:
      'Convert PNG, WebP, GIF, and other formats to optimised JPG for smaller file sizes and wider compatibility.',
    color: '#D97706',
    bg: 'rgba(217, 119, 6, 0.08)',
    border: 'rgba(217, 119, 6, 0.15)',
  },
  {
    icon: Zap,
    title: 'Instant Processing',
    description:
      'All tools run directly in your browser, no waiting for server uploads. Processing starts the moment you choose a file.',
    color: '#732EEC',
    bg: 'rgba(115, 46, 236, 0.08)',
    border: 'rgba(115, 46, 236, 0.15)',
  },
  {
    icon: Shield,
    title: 'Secure & Private',
    description:
      'Your files are processed locally or in your private cloud storage. No third parties ever touch your documents.',
    color: '#0891B2',
    bg: 'rgba(8, 145, 178, 0.08)',
    border: 'rgba(8, 145, 178, 0.15)',
  },
];

function FeatureCard({ feature, index }) {
  const [ref, inView] = useInView(0.1);
  const Icon = feature.icon;

  return (
    <div
      ref={ref}
      className="card-hover rounded-2xl p-6 border border-slate-100 bg-white shadow-sm"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.55s ease ${index * 0.07}s, transform 0.55s ease ${index * 0.07}s`,
      }}
    >
      {/* Icon */}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: feature.bg, border: `1px solid ${feature.border}` }}
      >
        <Icon size={22} style={{ color: feature.color }} />
      </div>

      {/* Text */}
      <h3 className="text-base font-bold text-slate-900 mb-2 tracking-tight">
        {feature.title}
      </h3>
      <p className="text-sm text-slate-500 leading-relaxed">
        {feature.description}
      </p>
    </div>
  );
}

export default function FeaturesSection() {
  const [headRef, headInView] = useInView();

  return (
    <section id="features" className="py-24 sm:py-32 bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        {/* Heading */}
        <div
          ref={headRef}
          className="text-center mb-16"
          style={{
            opacity: headInView ? 1 : 0,
            transform: headInView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-blue/8 text-brand-blue text-xs font-bold tracking-widest uppercase mb-4">
            Features
          </span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Everything You Need,{' '}
            <span className="text-gradient">Nothing You Don't</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-xl mx-auto leading-relaxed">
            A focused suite of powerful file tools built for speed, simplicity,
            and reliability.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => (
            <FeatureCard key={feature.title} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
